import { forwardRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Icon, type IconName } from './Icon';
import type { ButtonSize, ButtonVariant } from './Button';

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-action text-action-contrast border border-action hover:bg-action-hover',
  secondary: 'bg-surface text-content-secondary border border-line hover:bg-surface-hover',
  ghost:
    'bg-transparent text-content-secondary border border-transparent hover:bg-surface-hover hover:text-content',
  danger: 'bg-surface text-danger border border-line hover:bg-danger-subtle',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-7 w-7 rounded-md',
  md: 'h-9 w-9 rounded-md',
};

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Required: an icon-only control has no other accessible name. */
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  active?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'ghost', size = 'md', active, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center justify-center transition-colors duration-fast ease-standard',
        'disabled:cursor-not-allowed disabled:text-content-disabled disabled:hover:bg-transparent',
        focusRing,
        sizeClasses[size],
        variantClasses[variant],
        active && 'bg-surface-selected text-action',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
    </button>
  );
});
